import { type InsuranceListItem, type InsuranceListQuery } from '@utazzo/shared';
import { UzBadge, UzButton, UzStatusBadge } from '@utazzo/ui';
import { FileSpreadsheet } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { listInsurance } from '../insurance.api';
import { useBulkDeleteInsurance, useDeleteInsurance, useInsuranceList } from '../use-insurance';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCountries } from '@/features/cities/use-cities';
import { useTargetCountryFilter } from '@/hooks/use-catalog-geo-filters';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const INSURANCE_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'SINGLE_TRIP', label: 'Single trip' },
  { value: 'ANNUAL_MULTI_TRIP', label: 'Annual multi-trip' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'SENIOR', label: 'Senior' },
  { value: 'GROUP', label: 'Group' },
  { value: 'ADVENTURE', label: 'Adventure' },
  { value: 'MEDICAL_ONLY', label: 'Medical only' },
] as const;

const COVERAGE_SCOPE_OPTIONS = [
  { value: '', label: 'All coverage scopes' },
  { value: 'DOMESTIC', label: 'Domestic' },
  { value: 'INTERNATIONAL', label: 'International' },
  { value: 'WORLDWIDE', label: 'Worldwide' },
  { value: 'WORLDWIDE_EXCLUDING_US_CANADA', label: 'Worldwide (excl. US/Canada)' },
] as const;

const APPROVAL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'insuranceType',
    label: 'Insurance type',
    options: INSURANCE_TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-52',
  },
  {
    id: 'coverageScope',
    label: 'Coverage scope',
    options: COVERAGE_SCOPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-52',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const INSURANCE_TYPE_LABEL: Record<InsuranceListItem['insuranceType'], string> = {
  SINGLE_TRIP: 'Single trip',
  ANNUAL_MULTI_TRIP: 'Annual multi-trip',
  STUDENT: 'Student',
  SENIOR: 'Senior',
  GROUP: 'Group',
  ADVENTURE: 'Adventure',
  MEDICAL_ONLY: 'Medical only',
};

const COVERAGE_SCOPE_LABEL: Record<InsuranceListItem['coverageScope'], string> = {
  DOMESTIC: 'Domestic',
  INTERNATIONAL: 'International',
  WORLDWIDE: 'Worldwide',
  WORLDWIDE_EXCLUDING_US_CANADA: 'Worldwide (excl. US/CA)',
};

const mapStatus = (s: InsuranceListItem['status']): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

export const InsuranceListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteInsurance = useDeleteInsurance();
  const bulkDeleteMut = useBulkDeleteInsurance();
  const navigate = useNavigate();

  // A policy is sold FOR a country / region and has no city, so Target country
  // is this list's geography filter (server-side `targetCountryId`).
  const { data: countries } = useCountries();
  const targetCountryFilter = useTargetCountryFilter(countries);
  const filters = useMemo(() => [targetCountryFilter, ...FILTERS], [targetCountryFilter]);

  const columns: ListScreenColumns<InsuranceListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<InsuranceListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Insurance',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {row.legacyCode && <span className="text-xs text-zinc-500">{row.legacyCode}</span>}
          </div>
        ),
      },
      {
        id: 'target',
        header: 'Target country',
        cell: (row) => (
          <span className="text-sm text-zinc-900">
            {row.targetCountryName || <span className="text-zinc-400">Any</span>}
          </span>
        ),
      },
      {
        id: 'insuranceType',
        header: 'Type',
        sortable: true,
        width: '150px',
        cell: (row) => (
          <UzBadge tone="secondary">{INSURANCE_TYPE_LABEL[row.insuranceType]}</UzBadge>
        ),
      },
      {
        id: 'vendor',
        header: 'Underwriter',
        cell: (row) => (
          <span className="text-sm text-zinc-900">
            {row.vendorSupplierName ?? <span className="text-zinc-400">-</span>}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '140px',
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <UzStatusBadge status={mapStatus(row.status)} />
            {row.isApprovedByAdmin && (
              <span className="text-[10px] tracking-wide text-emerald-600 uppercase">
                Admin approved
              </span>
            )}
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<InsuranceListItem, InsuranceListQuery>
      title="Insurance"
      description="Tiered plan pricing (Basic / Standard / Comprehensive) per product."
      useList={useInsuranceList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const insuranceType = state.filters.insuranceType;
        const coverageScope = state.filters.coverageScope;
        const approval = state.filters.approval;
        const archived = state.filters.archived;
        const targetCountryId = state.filters.targetCountryId;
        return {
          ...base,
          ...(status ? { status: status as InsuranceListQuery['status'] } : {}),
          ...(targetCountryId ? { targetCountryId } : {}),
          ...(insuranceType
            ? { insuranceType: insuranceType as InsuranceListQuery['insuranceType'] }
            : {}),
          ...(coverageScope
            ? { coverageScope: coverageScope as InsuranceListQuery['coverageScope'] }
            : {}),
          ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
          ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
          ...(archived === 'archived' ? { includeDeleted: true } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add insurance', onClick: () => navigate('/insurance/new') }}
      headerExtra={
        <UzButton variant="outline" size="sm" onClick={() => navigate('/insurance/imports')}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" />
          Bulk import
        </UzButton>
      }
      onRowClick={(row) => navigate(`/insurance/${row.id}/edit`)}
      rowActions={{
        edit: false,
        delete: {
          onDelete: (row) => deleteInsurance.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this insurance?',
            description: `${row.name} will be soft-deleted (along with its pricing tiers).`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} insurance product${rows.length === 1 ? '' : 's'}?`,
            description: 'These products will be soft-deleted along with their pricing tiers.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listInsurance(q),
        filename: 'insurance',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Code', value: (r) => r.legacyCode ?? '' },
          { header: 'Target country', value: (r) => r.targetCountryName },
          { header: 'Insurance type', value: (r) => INSURANCE_TYPE_LABEL[r.insuranceType] },
          { header: 'Coverage scope', value: (r) => COVERAGE_SCOPE_LABEL[r.coverageScope] },
          { header: 'Coverage days', value: (r) => r.maxCoverageDays },
          { header: 'Coverage amount', value: (r) => r.coverageAmountLabel ?? '' },
          { header: 'Underwriter', value: (r) => r.vendorSupplierName ?? '' },
          { header: 'Status', value: (r) => r.status },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name, code, target country or underwriter (min 3 characters)',
        emptyTitle: 'No insurance products match these filters',
        emptyDescription: 'Adjust filters or clear the search box.',
        errorTitle: 'Failed to load insurance products',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
